import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchCategory,
  fetchArticles,
  fetchComparisons,
  fetchProducts,
} from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import ComparisonCard from "../components/ComparisonCard";
import ProductCard from "../components/ProductCard";
import Breadcrumbs from "../components/Breadcrumbs";

export default function CategoryDetail() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [guides, setGuides] = useState([]);
  const [comps, setComps] = useState([]);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCat(null);
    setNotFound(false);
    fetchCategory(slug)
      .then((c) => {
        setCat(c);
        document.title = `${c.name} | SaaSRadarHub`;
        fetchArticles({ type: "review", category: c.name }).then(setReviews);
        fetchArticles({ type: "guide", category: c.name }).then(setGuides);
        fetchComparisons({ category: c.name }).then(setComps);
        fetchProducts({ category: c.name }).then(setProducts);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <h1 className="font-playfair text-3xl mb-3">Category not found</h1>
        <Link to="/categories" className="text-indigo-800 underline">Back to categories</Link>
      </div>
    );
  }
  if (!cat) return <div className="max-w-3xl mx-auto px-5 py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="category-detail">
      <Breadcrumbs items={[{ label: "Categories", to: "/categories" }, { label: cat.name }]} />
      <header className="mb-14 max-w-3xl">
        <p className="eyebrow mb-3">Category</p>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight leading-[1.05] mb-5">
          {cat.name}
        </h1>
        <p className="text-lg text-neutral-600 leading-relaxed mb-5">{cat.intro}</p>
        <p className="text-base text-neutral-700 leading-relaxed">{cat.description}</p>
      </header>

      {products.length > 0 && (
        <section className="mb-20">
          <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Featured products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="mb-20">
          <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Latest reviews</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reviews.map((a, i) => <ArticleCard key={a.id} article={a} basePath="/reviews" index={i} />)}
          </div>
        </section>
      )}

      {comps.length > 0 && (
        <section className="mb-20">
          <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {comps.map((c) => <ComparisonCard key={c.id} comparison={c} />)}
          </div>
        </section>
      )}

      {guides.length > 0 && (
        <section className="mb-20">
          <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {guides.map((a, i) => <ArticleCard key={a.id} article={a} basePath="/guides" index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
